/**
 * Round artwork on the client — preload during the arm window, open at the
 * rung key.
 *
 * THE PROBLEM THIS SOLVES (invariant 2). Rung 4 is timed. Spec 8.2 pre-delivers
 * the prompt as ciphertext at T-500ms and releases the key at one server
 * instant, so that two players on two different pipes start thinking together.
 * The flag artwork used to arrive separately, from a third-party CDN, at
 * whatever speed each player's network and cache happened to give — which made
 * the guarantee false for the one rung where the stakes are highest. Now the
 * bytes come down during the same arm window as the ciphertext, from our own
 * gateway, and by the reveal instant both players are holding them.
 *
 * THE OTHER HALF (invariant 5). What comes down is sealed with the key of the
 * rung it belongs to. Preloading therefore buys the pixels and not one bit of
 * the answer: a client that pulls four flags at T-500ms is holding four blobs
 * it cannot look at until it has committed to the rung that shows them. The
 * token it fetched them with is 128 bits of randomness, per round and per seat,
 * and names no country, no file and no position.
 *
 * The store is deliberately dumb about game state: it takes tokens, holds
 * bytes, and turns bytes into object URLs when handed a key.
 */

import { openSealedBytes } from './crypto';

/** How the bytes are obtained. The transport owns the "where". */
export type AssetFetcher = (token: string) => Promise<Uint8Array>;

export interface PreloadReport {
  readonly tokens: number;
  readonly ok: number;
  readonly bytes: number;
  /** Wall-clock from the first request to the last completion. */
  readonly elapsedMs: number;
}

const LENGTH_PREFIX_BYTES = 4;

/**
 * Gunzip, via `Response` rather than `Blob.stream()`.
 *
 * `Blob.stream()` is the obvious spelling and it is missing in jsdom, which
 * means the obvious spelling is one the test suite cannot execute — and an
 * inflate path that only runs in a browser is an inflate path whose bugs only
 * appear in production. `new Response(bytes).body` is available everywhere this
 * code runs and pipes into the same DecompressionStream.
 */
async function inflate(gz: Uint8Array): Promise<Uint8Array> {
  const body = new Response(gz as BodyInit).body;
  if (body === null) throw new Error('no stream');
  const out = await new Response(body.pipeThrough(new DecompressionStream('gzip'))).arrayBuffer();
  return new Uint8Array(out);
}

/**
 * Sealed bundle -> the SVG source of each option, in option order.
 *
 * Pure, and exported so it can be tested against bytes the REAL gateway sealed
 * without a DOM in the way. `open()` is this plus object URLs.
 */
export async function decodeAssetBundle(
  sealed: Uint8Array,
  keyB64: string,
): Promise<readonly string[] | null> {
  const plain = await openSealedBytes(sealed, keyB64);
  if (plain === null || plain.length < LENGTH_PREFIX_BYTES) return null;

  const view = new DataView(plain.buffer, plain.byteOffset, plain.byteLength);
  const length = view.getUint32(0);
  if (length === 0 || LENGTH_PREFIX_BYTES + length > plain.length) return null;

  let svgs: unknown;
  try {
    const json = await inflate(plain.subarray(LENGTH_PREFIX_BYTES, LENGTH_PREFIX_BYTES + length));
    svgs = JSON.parse(new TextDecoder().decode(json)) as unknown;
  } catch {
    return null;
  }
  if (!Array.isArray(svgs) || svgs.length === 0) return null;
  if (!svgs.every((s) => typeof s === 'string')) return null;
  return svgs as string[];
}

/**
 * Fisher-Yates over a copy. The request order carries no information as it
 * stands — one bundle per rung, and which rung is inside the seal — but a
 * fixed order is a habit worth not having if the list ever grows.
 */
function shuffled<T>(items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j] as T, out[i] as T];
  }
  return out;
}

export class RoundAssets {
  /** token -> sealed bytes, as fetched. */
  private readonly sealed = new Map<string, Uint8Array>();
  /** token -> in-flight fetch, so a resync's repeat preload is free. */
  private readonly inflight = new Map<string, Promise<void>>();
  /** token -> object URLs, one per option, in option order. */
  private readonly opened = new Map<string, readonly string[]>();
  private lastReport: PreloadReport | null = null;

  /**
   * Pull every token's bytes. Resolves when all have landed or failed — the
   * caller does NOT await this on the reveal path, because a slow asset must
   * never hold up the prompt. It is fired at arm time and simply expected to
   * have finished by the time a picture rung opens, several seconds later.
   */
  async preload(tokens: readonly string[], fetcher: AssetFetcher): Promise<PreloadReport> {
    const started = Date.now();
    let ok = 0;
    let bytes = 0;

    await Promise.all(
      shuffled(tokens).map(async (token) => {
        let job = this.inflight.get(token);
        if (job === undefined && !this.sealed.has(token)) {
          job = fetcher(token)
            .then((buf) => {
              this.sealed.set(token, buf);
            })
            .catch(() => undefined)
            .finally(() => {
              this.inflight.delete(token);
            });
          this.inflight.set(token, job);
        }
        if (job !== undefined) await job;
        const got = this.sealed.get(token);
        if (got !== undefined) {
          ok += 1;
          bytes += got.length;
        }
      }),
    );

    const report = { tokens: tokens.length, ok, bytes, elapsedMs: Date.now() - started };
    this.lastReport = report;
    return report;
  }

  /** What the last preload cost. Read by the timing harness, not by the UI. */
  report(): PreloadReport | null {
    return this.lastReport;
  }

  has(token: string): boolean {
    return this.sealed.has(token);
  }

  /**
   * Decrypt one bundle and hand back one object URL per option, in option
   * order. Idempotent; null when the bytes never arrived, the key is wrong, or
   * the blob is malformed.
   *
   * A late fetch is tolerated on purpose: if the preload missed (a drop, a
   * resync mid-round), this pulls the bytes now rather than rendering a rung
   * with no pictures on it. That path is SLOWER and therefore unfair, which is
   * exactly why the preload exists — but a slow flag beats an unanswerable one.
   */
  async open(
    token: string,
    keyB64: string,
    fetcher?: AssetFetcher,
  ): Promise<readonly string[] | null> {
    const already = this.opened.get(token);
    if (already !== undefined) return already;

    let bytes = this.sealed.get(token);
    if (bytes === undefined && fetcher) {
      await this.preload([token], fetcher);
      bytes = this.sealed.get(token);
    }
    if (bytes === undefined) return null;

    const svgs = await decodeAssetBundle(bytes, keyB64);
    if (svgs === null) return null;

    // An object URL, not a data: URI: a blob keeps the bytes out of the DOM,
    // so the markup an inspector sees is a meaningless `blob:` handle rather
    // than the artwork itself. Rendered through <img>, where SVG cannot script.
    const urls = svgs.map((svg) =>
      URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' })),
    );
    this.opened.set(token, urls);
    return urls;
  }

  /** Revoke every object URL and forget every byte. Called on teardown. */
  release(): void {
    for (const urls of this.opened.values()) {
      for (const url of urls) URL.revokeObjectURL(url);
    }
    this.opened.clear();
    this.sealed.clear();
    this.inflight.clear();
    this.lastReport = null;
  }
}
